/**
 * مساعد كبسولة — the help assistant, on every screen.
 *
 * A lit orb in the corner opens a panel with two tabs:
 *
 *   المساعد الذكي   ask anything about the system; the answer streams in, the
 *                   model's thinking one tap away, the manual sections it was
 *                   grounded on named under it (Backend/routes/help.js — its
 *                   own RAG over the system's manual, on the same AI engine,
 *                   keys and model RBAC as the medical analysis). "الأسرع" or
 *                   "الأذكى" when the account was granted the paid model.
 *   الدعم الفني     messages to real people on the support team, with a
 *                   screenshot of the current screen attached automatically
 *                   (SupportCenter).
 *
 * The conversation is kept in this browser per account (localStorage —
 * convenience only, it can vanish), and the launcher shows a dot when
 * support has replied.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Icon } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { helpApi, supportApi } from '../../api/endpoints';
import { usePoll } from '../../hooks/usePolling';
import { streamAi } from '../ai/aiStream';
import { MAX_KEPT, historyForServer, messageId, replyBlocks, storageKey, suggestionsFor } from '../../domain/helpChat';
import { SupportCenter } from './SupportCenter';

const loadStored = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((m) => m && m.status !== 'streaming').slice(-MAX_KEPT) : [];
  } catch {
    return [];
  }
};

const saveStored = (key, messages) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(messages.filter((m) => m.status !== 'streaming').slice(-MAX_KEPT)));
  } catch {
    /* storage refused (private mode, quota) — the chat still works */
  }
};

function Segments({ segments }) {
  return segments.map((segment, i) => {
    if (segment.kind === 'bold') return <strong key={i}>{segment.text}</strong>;
    if (segment.kind === 'ui') return <span key={i} className="help-ui-name">{segment.text}</span>;
    if (segment.kind === 'code') return <code key={i} className="help-code">{segment.text}</code>;
    return <span key={i}>{segment.text}</span>;
  });
}

function Reply({ text }) {
  const blocks = useMemo(() => replyBlocks(text), [text]);
  return (
    <div className="help-reply">
      {blocks.map((block, i) => {
        if (block.type === 'step') {
          return (
            <p key={i} className="help-step">
              <span className="help-step__n">{block.n}</span>
              <span className="min-w-0 flex-1">
                <Segments segments={block.segments} />
              </span>
            </p>
          );
        }
        if (block.type === 'bullet') {
          return (
            <p key={i} className="help-bullet">
              <span className="help-bullet__dot" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <Segments segments={block.segments} />
              </span>
            </p>
          );
        }
        if (block.type === 'heading') {
          return (
            <p key={i} className="help-heading">
              <Segments segments={block.segments} />
            </p>
          );
        }
        return (
          <p key={i}>
            <Segments segments={block.segments} />
          </p>
        );
      })}
    </div>
  );
}

function Thinking({ message }) {
  const [open, setOpen] = useState(false);
  const text = message.reasoning || '';
  const streaming = message.status === 'streaming';
  if (!text && !(streaming && !message.content)) return null;
  return (
    <div className="help-thinking">
      <button type="button" className="help-thinking__head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <Icon name="brain" size={13} className={streaming && !message.content ? 'animate-pulse' : ''} />
        <span>{streaming && !message.content ? 'يفكّر…' : 'مسار التفكير'}</span>
        {text && <span className="help-thinking__count">{text.length} حرف</span>}
        <span className="flex-1" />
        {text && <Icon name={open ? 'chevron-up' : 'chevron-down'} size={13} />}
      </button>
      {open && text && <div className="help-thinking__body custom-scrollbar">{text}</div>}
    </div>
  );
}

function AssistantChat({ role, userId }) {
  const location = useLocation();
  const key = storageKey(userId);
  const [messages, setMessages] = useState(() => loadStored(key));
  const [draft, setDraft] = useState('');
  const [premium, setPremium] = useState(false);
  const [caps, setCaps] = useState(null);
  const abortRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const streaming = messages.some((m) => m.status === 'streaming');

  useEffect(() => {
    helpApi
      .capabilities()
      .then(setCaps)
      .catch(() => setCaps({ available: true, canUsePremium: false }));
  }, []);

  useEffect(() => saveStored(key, messages), [key, messages]);
  useEffect(() => () => abortRef.current?.abort(), []);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const patch = (id, change) => setMessages((list) => list.map((m) => (m.id === id ? { ...m, ...change(m) } : m)));

  const send = useCallback(
    async (textArg) => {
      const question = String(textArg ?? draft).trim();
      if (!question || streaming) return;
      setDraft('');
      const user = { id: messageId(), role: 'user', content: question, at: Date.now() };
      const reply = { id: messageId(), role: 'assistant', content: '', reasoning: '', status: 'streaming', at: Date.now(), meta: {} };
      const history = historyForServer([...messages, user]);
      setMessages((list) => [...list, user, reply].slice(-MAX_KEPT));

      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const result = await streamAi(
          '/help/chat',
          { messages: history, page: location.pathname, premium },
          {
            signal: controller.signal,
            onContent: (_t, full) => patch(reply.id, () => ({ content: full })),
            onReasoning: (_t, full) => patch(reply.id, () => ({ reasoning: full })),
            onMeta: (_f, meta) => patch(reply.id, () => ({ meta })),
          },
        );
        patch(reply.id, (m) => ({
          status: result.aborted ? 'stopped' : 'done',
          content: m.content || result.content || (result.error ? `تعذر الرد: ${result.error}` : ''),
        }));
      } catch (err) {
        patch(reply.id, () => ({ status: 'error', content: err.message || 'تعذر الاتصال بالمساعد' }));
      } finally {
        abortRef.current = null;
        inputRef.current?.focus();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, streaming, messages, location.pathname, premium],
  );

  const stop = () => abortRef.current?.abort();
  const clear = () => {
    stop();
    setMessages([]);
  };

  const canPremium = Boolean(caps && caps.canUsePremium);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="help-toolbar">
        <div className="ai-model-switch" role="group" aria-label="نموذج المساعد">
          <button type="button" data-tier="standard" aria-pressed={!premium} onClick={() => setPremium(false)}>
            <Icon name="zap" size={13} /> الأسرع
          </button>
          <button
            type="button"
            data-tier="premium"
            aria-pressed={premium}
            disabled={!canPremium}
            onClick={() => setPremium(true)}
            title={canPremium ? 'إجابة أعمق بالنموذج المتقدم' : 'النموذج الأذكى غير مُفعّل لحسابك'}
          >
            <Icon name={canPremium ? 'crown' : 'lock'} size={13} /> الأذكى
          </button>
        </div>
        <span className="flex-1" />
        {messages.length > 0 && (
          <button type="button" className="help-icon-btn" onClick={clear} title="محادثة جديدة">
            <Icon name="rotate-ccw" size={15} />
          </button>
        )}
      </div>

      <div ref={listRef} className="help-messages custom-scrollbar" aria-live="polite">
        {messages.length === 0 ? (
          <div className="help-welcome">
            <div className="help-welcome__orb" aria-hidden="true">
              <Icon name="sparkles" size={26} />
            </div>
            <p className="text-base font-black text-white">أهلاً بك! أنا مساعد كبسولة</p>
            <p className="text-xs text-slate-400">اسألني عن أي شاشة أو خطوة في النظام وأشرحها لك خطوة بخطوة.</p>
            <div className="help-suggestions">
              {suggestionsFor(role).map((q) => (
                <button key={q} type="button" className="help-chip" onClick={() => send(q)}>
                  <Icon name="message-circle" size={13} /> {q}
                </button>
              ))}
            </div>
            {caps && caps.available === false && (
              <p className="mt-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                المساعد الذكي غير مُهيّأ على الخادم حالياً — تبويب «الدعم الفني» متاح.
              </p>
            )}
          </div>
        ) : (
          messages.map((m) =>
            m.role === 'user' ? (
              <div key={m.id} className="help-bubble help-bubble--user">
                {m.content}
              </div>
            ) : (
              <div key={m.id} className={`help-bubble help-bubble--bot ${m.status === 'error' ? 'help-bubble--error' : ''}`}>
                <div className="help-bubble__who">
                  <span className="help-bubble__avatar" aria-hidden="true">
                    <Icon name="bot" size={13} />
                  </span>
                  مساعد كبسولة
                  {m.meta?.tier === 'premium' && (
                    <span className="help-tier">
                      <Icon name="crown" size={10} /> الأذكى
                    </span>
                  )}
                </div>
                <Thinking message={m} />
                {m.meta?.downgradeMessage && <p className="help-note">{m.meta.downgradeMessage}</p>}
                {m.content ? <Reply text={m.content} /> : m.status === 'streaming' ? <span className="help-typing"><i /><i /><i /></span> : null}
                {m.status === 'streaming' && m.content && <span className="ai-caret" />}
                {m.status === 'stopped' && <p className="help-note">تم الإيقاف</p>}
                {Array.isArray(m.meta?.sources) && m.meta.sources.length > 0 && m.status !== 'streaming' && (
                  <div className="help-sources">
                    <Icon name="book-open" size={11} /> من دليل النظام:
                    {m.meta.sources.slice(0, 3).map((s) => (
                      <span key={s.id} className="help-source">
                        {s.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ),
          )
        )}
      </div>

      <form
        className="help-composer"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <textarea
          ref={inputRef}
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="اكتب سؤالك… مثال: إزاي أطبع جدول العيادات؟"
          aria-label="سؤالك للمساعد"
          className="help-input"
          maxLength={2000}
        />
        {streaming ? (
          <button type="button" className="help-send help-send--stop" onClick={stop} aria-label="إيقاف">
            <Icon name="square" size={16} />
          </button>
        ) : (
          <button type="submit" className="help-send" disabled={!draft.trim()} aria-label="إرسال">
            <Icon name="send" size={16} />
          </button>
        )}
      </form>
      <p className="px-4 pb-2 text-center text-[10px] text-slate-500">يشرح النظام فقط ولا يقدم استشارات طبية. للمشكلات استخدم «الدعم الفني».</p>
    </div>
  );
}

export function HelpAssistant() {
  const { session, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('assistant');
  const [unread, setUnread] = useState(0);
  const role = session?.role || null;

  const loadUnread = useCallback(() => supportApi.unread().then((r) => setUnread(Number(r.count) || 0)), []);
  useEffect(() => {
    if (isAuthenticated) loadUnread().catch(() => {});
  }, [isAuthenticated, loadUnread]);
  usePoll(loadUnread, isAuthenticated ? 60000 : null, { immediate: false });

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Other screens can open the assistant on a tab ("تواصل مع الدعم").
  useEffect(() => {
    const onOpen = (e) => {
      setOpen(true);
      if (e.detail && e.detail.tab) setTab(e.detail.tab);
    };
    window.addEventListener('capsule:help', onOpen);
    return () => window.removeEventListener('capsule:help', onOpen);
  }, []);

  if (!isAuthenticated) return null;

  return (
    <>
      <button
        type="button"
        className={`help-launcher ${open ? 'help-launcher--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'إغلاق المساعد' : 'المساعد الذكي والدعم الفني'}
        title="المساعد الذكي والدعم الفني"
      >
        <span className="help-launcher__ring" aria-hidden="true" />
        <Icon name={open ? 'x' : 'sparkles'} size={22} />
        {!open && unread > 0 && <span className="help-launcher__dot">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <section className="help-dock" role="dialog" aria-label="المساعد الذكي">
          <header className="help-dock__head">
            <span className="help-dock__orb" aria-hidden="true">
              <Icon name="bot" size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-black text-white">مساعد كبسولة</p>
              <p className="truncate text-[11px] text-slate-400">يعرف كل شاشات النظام — أو تواصل مع فريق الدعم</p>
            </div>
            <button type="button" className="help-icon-btn" onClick={() => setOpen(false)} aria-label="إغلاق">
              <Icon name="x" size={16} />
            </button>
          </header>
          <div className="help-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'assistant'} className={tab === 'assistant' ? 'help-tab help-tab--on' : 'help-tab'} onClick={() => setTab('assistant')}>
              <Icon name="sparkles" size={14} /> المساعد الذكي
            </button>
            <button type="button" role="tab" aria-selected={tab === 'support'} className={tab === 'support' ? 'help-tab help-tab--on' : 'help-tab'} onClick={() => setTab('support')}>
              <Icon name="headset" size={14} /> الدعم الفني
              {unread > 0 && <span className="help-tab__dot">{unread}</span>}
            </button>
          </div>
          {tab === 'assistant' ? (
            <AssistantChat role={role} userId={session?.id} />
          ) : (
            <SupportCenter onRead={() => loadUnread().catch(() => {})} />
          )}
          {tab === 'assistant' && (
            <button type="button" className="help-handoff" onClick={() => setTab('support')}>
              <Icon name="headset" size={14} /> محتاج حد من فريق الدعم؟ ابعت رسالة مع لقطة للشاشة
            </button>
          )}
        </section>
      )}
    </>
  );
}

export default HelpAssistant;
